/*
 * Copyright (c) 2016-2023 VMware, Inc. All Rights Reserved.
 * This software is released under MIT license.
 * The full license information can be found in LICENSE in the root directory of this project.
 */
import { html } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import { stopEvent } from '../utils/events.js';
import { onAnyKey } from '../utils/keycodes.js';
import { renderAfter } from '../utils/lit.js';
/**
 * Shim for allowing native submit type events with custom element buttons as Safari prevents use of extending native element types
 */
export function buttonSubmit() {
    return (target) => target.addInitializer((instance) => new ButtonSubmitController(instance));
}
export class ButtonSubmitController {
    constructor(host) {
        this.host = host;
        this.triggerNativeButtonBehaviorHandler = this.triggerNativeButtonBehavior.bind(this);
        this.emulateKeyBoardEventBehaviorHandler = this.emulateKeyBoardEventBehavior.bind(this);
        this.host.addController(this);
    }
    hostUpdated() {
        this.setButtonType();
        this.setupNativeButtonBehavior();
    }
    setButtonType() {
        if (!this.host.type && this.host.closest('form')) {
            this.host.type = 'submit';
        }
    }
    setupNativeButtonBehavior() {
        if (this.host.readonly || this.host.disabled) {
            this.host.removeEventListener('click', this.triggerNativeButtonBehaviorHandler);
            this.host.removeEventListener('keyup', this.emulateKeyBoardEventBehaviorHandler);
        }
        else {
            this.host.addEventListener('click', this.triggerNativeButtonBehaviorHandler);
            this.host.addEventListener('keyup', this.emulateKeyBoardEventBehaviorHandler);
        }
    }
    emulateKeyBoardEventBehavior(evt) {
        onAnyKey(['enter', 'space'], evt, () => {
            // When submitting forms with Enter key, the default submit button receives a click event from the form.
            if (this.host.type === 'submit') {
                this.triggerNativeButtonBehavior(evt);
            }
            else {
                this.host.click();
            }
            stopEvent(evt);
        });
    }
    /**
     * We have to append a hidden button outside the web component in the light DOM
     * This allows us to trigger native submit events within a form element.
     */
    triggerNativeButtonBehavior(event) {
        if (this.host.disabled) {
            stopEvent(event);
        }
        else if (!event.defaultPrevented) {
            const submitButton = renderAfter(html `<button
          aria-hidden="true"
          role="presentation"
          ?disabled="${this.host.disabled}"
          tabindex="-1"
          style="display: none !important"
          value="${ifDefined(this.host.value)}"
          name="${ifDefined(this.host.name)}"
          type="${ifDefined(this.host.type)}"
        ></button>`, this.host);
            submitButton?.dispatchEvent(new MouseEvent('click', { relatedTarget: this.host, composed: true }));
            submitButton?.remove();
        }
    }
}
